/**
 * @file Серверный протокол скачивания и подготовки архивов.
 *
 * HEAD получает метаданные, GET читает и расшифровывает диапазон. Квота берётся
 * из локального снимка; длины и статусы проверяются отдельно.
 */
import { abortableDelay } from '../../services/abort.ts'
import { decrypt, generate16RandomBytes } from '../../services/cipher.ts'
import { b64Encode } from '../../services/encoding.ts'
import { makeTunnelRequest } from '../../services/request.ts'
import { waitForTask } from '../../services/task.ts'
import { isTaskId } from '../../services/taskId.ts'
import { cachedAvailableTraffic } from '../transfers/snapshots.ts'
import {
    DownloadError,
    checkDownloadResponse,
    parseDownloadMetadata,
    readBoundedBody,
    verifyMetadata,
    verifyRangeResponse,
} from './model.ts'
import type {
    DownloadMetadata,
    DownloadRange,
    DownloadRequest,
    DownloadSource,
} from './model.ts'

function tunnel(
    request: DownloadRequest,
    path: string,
    opts: RequestInit,
    signal: AbortSignal,
) {
    return makeTunnelRequest({
        path,
        boxID: request.boxId,
        token: request.token,
        opts: {
            ...opts,
            signal: AbortSignal.any([signal, AbortSignal.timeout(60_000)]),
        },
    })
}

export async function metadata(
    request: DownloadRequest,
    source: DownloadSource,
    signal: AbortSignal,
): Promise<DownloadMetadata> {
    const response = await tunnel(
        request,
        '/download',
        {
            method: 'HEAD',
            cache: 'no-store',
            headers: { 'X-File-Path': b64Encode(source.path) },
        },
        signal,
    )

    return parseDownloadMetadata(response)
}

export async function checkQuota(
    request: DownloadRequest,
    meta: DownloadMetadata,
    signal: AbortSignal,
): Promise<void> {
    signal.throwIfAborted()

    // Это последний известный остаток, а не новый запрос API или резервирование.
    const available = cachedAvailableTraffic(request.boxId)
    if (available === null) throw new DownloadError('quotaCheck')

    if (available < meta.size) throw new DownloadError('quota')
}

export async function fetchRange(
    request: DownloadRequest,
    source: DownloadSource,
    range: DownloadRange,
    signal: AbortSignal,
): Promise<Uint8Array<ArrayBuffer>> {
    let lastError: unknown

    for (let attempt = 0; attempt < 3; attempt++) {
        signal.throwIfAborted()

        try {
            const iv = generate16RandomBytes()
            const headers: Record<string, string> = {
                Range: `bytes=${range.start}-${range.end - 1}`,
                'X-Initialization-Vector': btoa(String.fromCharCode(...iv)),
                'X-File-Path': b64Encode(source.path),
            }
            const bounded = AbortSignal.any([
                signal,
                AbortSignal.timeout(60_000),
            ])
            const response = await tunnel(
                request,
                '/download',
                { headers, cache: 'no-store' },
                bounded,
            )

            const plainSize = range.end - range.start
            // На каждый зашифрованный чанк приходятся salt 16 байт и padding AES-CBC.
            const encodedSize =
                request.password === ''
                    ? plainSize
                    : 16 + 16 * (Math.floor(plainSize / 16) + 1)
            try {
                verifyRangeResponse(response, encodedSize)
            } catch (error) {
                void response.body?.cancel().catch(() => {})

                throw error
            }

            const body = await readBoundedBody(response, encodedSize, bounded)
            let result: ArrayBuffer

            try {
                result = await decrypt({
                    encryptedBytes: body,
                    password: request.password,
                    iv,
                })
            } catch {
                throw new DownloadError('decryption')
            }

            signal.throwIfAborted()
            if (result.byteLength !== plainSize)
                throw new DownloadError('incomplete')

            return new Uint8Array(result)
        } catch (error) {
            signal.throwIfAborted()
            if (
                error instanceof DownloadError &&
                !['network', 'rateLimited'].includes(error.code)
            )
                throw error

            lastError = error
            if (attempt < 2) await abortableDelay(500 * 2 ** attempt, signal)
        }
    }

    throw lastError instanceof DownloadError
        ? lastError
        : new DownloadError('network')
}

export async function verify(
    request: DownloadRequest,
    source: DownloadSource,
    before: DownloadMetadata,
    signal: AbortSignal,
): Promise<void> {
    verifyMetadata(before, await metadata(request, source, signal))
}

async function waitArchive(
    request: DownloadRequest,
    id: string,
    signal: AbortSignal,
): Promise<void> {
    const result = await waitForTask(
        (signal) =>
            tunnel(request, `/task/${id}`, { cache: 'no-store' }, signal),
        signal,
    )
    if (result === 'done') return
    if (result === 'access') throw new DownloadError('access')

    throw new DownloadError(
        result === 'failed' ? 'archiveFailed' : 'archiveUnknown',
    )
}

export async function prepare(
    request: DownloadRequest,
    signal: AbortSignal,
): Promise<DownloadSource> {
    if (request.kind === 'file')
        return { path: request.path, name: request.name, temporary: false }

    const response = await tunnel(
        request,
        '/folder/download',
        {
            method: 'POST',
            headers: { 'x-folder-path': b64Encode(request.path) },
        },
        signal,
    )
    checkDownloadResponse(response)
    const task = response.headers.get('X-Task-ID')
    const path = response.headers.get('X-Archive-Path')
    if (
        !isTaskId(task) ||
        !path ||
        !path.startsWith('/') ||
        path.includes('\0') ||
        path.split('/').some((part) => part === '.' || part === '..') ||
        path === request.path ||
        request.path.startsWith(path + '/')
    )
        throw new DownloadError('protocol')

    try {
        await waitArchive(
            request,
            task,
            AbortSignal.any([signal, AbortSignal.timeout(5 * 60_000)]),
        )
    } catch (error) {
        signal.throwIfAborted()
        if (error instanceof DownloadError) throw error

        throw new DownloadError('archiveUnknown')
    }

    const name = path.split('/').at(-1)
    if (!name) throw new DownloadError('protocol')

    return { path, name, temporary: true }
}

export async function cleanup(
    request: DownloadRequest,
    source: DownloadSource,
): Promise<void> {
    // Удаляем только созданный архив. Родительский каталог может содержать другие файлы.
    if (!source.temporary) return

    const bounded = AbortSignal.timeout(10_000)
    const response = await tunnel(
        request,
        '/file',
        {
            method: 'DELETE',
            headers: { 'x-file-path': b64Encode(source.path) },
        },
        bounded,
    )
    if (response.status === 404) return

    checkDownloadResponse(response)
    const id = response.headers.get('X-Task-ID')
    if (!isTaskId(id)) throw new DownloadError('protocol')

    await waitArchive(request, id, bounded)
}
